import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector, Field } from 'redux-form';

import {
    SelectFieldComponent
} from '../../../form';
import ButtonComponent from '../../../button/ButtonComponent';

import { getStatusOptions, shouldShowBalanceInput, mapEbonStateToSelectValue } from '../../../../utils/ebonStatusUtils';

import './EbonStatusUpdateForm.scss';

const FORM_NAME = 'ebon-status-update-form';
const AMOUNT_REGEX = /^\d+([.,]\d{0,2})?$/;

class EbonStatusUpdateForm extends React.Component {
    state = {
        balanceError: null
    };

    componentDidMount() {
        const { initialize, initialBalance, ebonList } = this.props;
        const firstEbon = ebonList && ebonList.length > 0 ? ebonList[0] : null;
        const consumedAmount = firstEbon && initialBalance !== undefined && initialBalance !== null
            ? (firstEbon.facialValue - parseFloat(initialBalance)).toFixed(2).replace('.', ',')
            : '';

        initialize({
            selectedEbonId: firstEbon ? String(firstEbon.id) : '',
            status: firstEbon ? mapEbonStateToSelectValue(firstEbon.userState) : '',
            remainingBalance: consumedAmount
        });
    }

    componentDidUpdate(prevProps) {
        const { change, statusValue, selectedEbonIdValue, ebonList } = this.props;

        if (prevProps.statusValue !== statusValue) {
            if (statusValue !== 'CURRENTLY_IN_USE' && statusValue !== 'PENDING') {
                change('remainingBalance', '');
                this.setState({ balanceError: null });
            }
        }

        if (prevProps.selectedEbonIdValue !== selectedEbonIdValue && selectedEbonIdValue && ebonList) {
            const selectedEbon = ebonList.find(ebon => String(ebon.id) === selectedEbonIdValue);
            if (selectedEbon) {
                change('status', mapEbonStateToSelectValue(selectedEbon.userState));
                if (selectedEbon.remainingBalance !== undefined && selectedEbon.remainingBalance !== null) {
                    const consumedAmount = (selectedEbon.facialValue - parseFloat(selectedEbon.remainingBalance)).toFixed(2).replace('.', ',');
                    change('remainingBalance', consumedAmount);
                } else {
                    change('remainingBalance', '');
                }
                this.setState({ balanceError: null });
            }
        }
    }

    handleSubmit = (values) => {
        const { onSubmit, ebonList, i18nPage } = this.props;

        if (ebonList.length === 1) {
            const selectedEbon = ebonList[0];

            if (values.remainingBalance) {
                const consumedAmount = parseFloat(values.remainingBalance.replace(',', '.'));
                if (!isNaN(consumedAmount) && consumedAmount > selectedEbon.facialValue) {
                    const formattedTotal = selectedEbon.facialValue.toFixed(2).replace('.', ',');
                    this.setState({
                        balanceError: i18nPage.FORM.BALANCE_EXCEEDS_TOTAL.replace('{0}', formattedTotal)
                    });
                    return;
                }

                const remainingBalance = (selectedEbon.facialValue - consumedAmount).toFixed(2).replace('.', ',');
                this.setState({ balanceError: null });
                onSubmit(values.status, remainingBalance);
            } else {
                this.setState({ balanceError: null });
                onSubmit(values.status, null);
            }
        } else {
            if (!values.selectedEbonId) {
                this.setState({
                    balanceError: i18nPage.FORM.EBON_VALIDATION_ERROR
                });
                return;
            }

            const selectedEbon = ebonList.find(ebon => String(ebon.id) === values.selectedEbonId);
            if (!selectedEbon) {
                console.error('Selected ebon not found. selectedEbonId:', values.selectedEbonId, 'ebonList:', ebonList.map(e => e.id));
                return;
            }

            if (values.remainingBalance) {
                const consumedAmount = parseFloat(values.remainingBalance.replace(',', '.'));
                if (!isNaN(consumedAmount) && consumedAmount > selectedEbon.facialValue) {
                    const formattedTotal = selectedEbon.facialValue.toFixed(2).replace('.', ',');
                    this.setState({
                        balanceError: i18nPage.FORM.BALANCE_EXCEEDS_TOTAL.replace('{0}', formattedTotal)
                    });
                    return;
                }

                const remainingBalance = (selectedEbon.facialValue - consumedAmount).toFixed(2).replace('.', ',');
                this.setState({ balanceError: null });
                onSubmit(values.selectedEbonId, values.status, remainingBalance);
            } else {
                this.setState({ balanceError: null });
                onSubmit(values.selectedEbonId, values.status, null);
            }
        }
    };

    validateSelectedEbon = (value) => {
        const { i18nPage } = this.props;
        if (!value) {
            return i18nPage.FORM.EBON_VALIDATION_ERROR;
        }
        return undefined;
    };

    validateStatus = (value) => {
        const { i18nPage } = this.props;
        if (!value) {
            return i18nPage.FORM.VALIDATION_ERROR;
        }
        return undefined;
    };

    getEbonOptions = () => {
        const { ebonList, i18nPage } = this.props;
        if (!ebonList || ebonList.length === 0) return [];

        return ebonList.map((ebon, index) => {
            const formattedValue = ebon.facialValue.toFixed(2).replace('.', ',');
            const showNumber = ebonList.length > 1 && ebonList.filter(e => e.facialValue === ebon.facialValue).length > 1;
            const baseLabel = i18nPage.FORM.EBON_SELECT_OPTION.replace('{0}', formattedValue);
            const label = showNumber ? `${index + 1} - ${baseLabel}` : baseLabel;

            return {
                label,
                value: String(ebon.id)
            };
        });
    };

    renderBalanceInput = ({ input }) => {
        const { i18nPage } = this.props;
        const { balanceError } = this.state;
        const inputId = `${input.name}-${FORM_NAME}`;
        const errorId = `error-${input.name}-${FORM_NAME}`;
        return (
            <div className="price-field">
                <label htmlFor={inputId} className="sr-only">
                    {i18nPage.FORM.REMAINING_BALANCE_PLACEHOLDER}
                </label>
                <div className="item">
                    <input
                        {...input}
                        id={inputId}
                        type="text"
                        placeholder={i18nPage.FORM.REMAINING_BALANCE_PLACEHOLDER}
                        className="input"
                        aria-invalid={balanceError ? 'true' : undefined}
                        aria-describedby={balanceError ? errorId : undefined}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || AMOUNT_REGEX.test(val)) {
                                input.onChange(val);
                                this.setState({ balanceError: null });
                            }
                        }}
                    />
                </div>
            </div>
        );
    };

    render() {
        const { handleSubmit, submitting, statusValue, disabled, i18nForm, i18nPage, ebonList } = this.props;
        const { balanceError } = this.state;
        const showBalanceInput = shouldShowBalanceInput(statusValue);
        const statusOptions = getStatusOptions(i18nPage);
        const ebonOptions = this.getEbonOptions();
        const showEbonSelect = ebonList && ebonList.length > 1;

        return (
            <div className="ebon-form">
                <form onSubmit={handleSubmit(this.handleSubmit)}>
                    <div className="fields">
                        {showEbonSelect && (
                            <div className="select-field">
                                <SelectFieldComponent
                                    label={i18nPage.FORM.EBON_SELECT_LABEL}
                                    name="selectedEbonId"
                                    defaultOptionLabel={i18nPage.FORM.EBON_SELECT_PLACEHOLDER}
                                    options={ebonOptions}
                                    validate={[this.validateSelectedEbon]}
                                    isRequired={true}
                                    hideRequired={true}
                                    formName={FORM_NAME}
                                    i18nFeatureForm={i18nForm}
                                />
                            </div>
                        )}
                        <div className="select-field">
                            <SelectFieldComponent
                                label={i18nPage.FORM.LABEL}
                                name="status"
                                defaultOptionLabel={i18nPage.FORM.SELECT_PLACEHOLDER}
                                options={statusOptions}
                                validate={[this.validateStatus]}
                                isRequired={true}
                                hideRequired={true}
                                formName={FORM_NAME}
                                i18nFeatureForm={i18nForm}
                            />
                        </div>
                        {showBalanceInput ? (
                            <div className="input-group">
                                <Field
                                    name="remainingBalance"
                                    component={this.renderBalanceInput}
                                />
                                <div className="button-field">
                                    <ButtonComponent
                                        type="submit"
                                        color="main"
                                        text={i18nPage.FORM.VALIDATE_BUTTON}
                                        disabled={submitting || disabled || !statusValue}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="button-field">
                                <ButtonComponent
                                    type="submit"
                                    color="main"
                                    text={i18nPage.FORM.VALIDATE_BUTTON}
                                    disabled={submitting || disabled || !statusValue}
                                />
                            </div>
                        )}
                    </div>
                </form>
                {balanceError && (
                    <div
                        className="balance-error"
                        id={`error-remainingBalance-${FORM_NAME}`}
                        role="alert">
                        {balanceError}
                    </div>
                )}
            </div>
        );
    }

}

EbonStatusUpdateForm.propTypes = {
    initialStatus: PropTypes.string,
    initialBalance: PropTypes.string,
    totalAmount: PropTypes.number,
    ebonList: PropTypes.array,
    onSubmit: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    // From redux-form
    handleSubmit: PropTypes.func,
    submitting: PropTypes.bool,
    initialize: PropTypes.func,
    change: PropTypes.func,
    // From formValueSelector
    statusValue: PropTypes.string,
    selectedEbonIdValue: PropTypes.string,
    // From state
    i18nForm: PropTypes.object,
    i18nPage: PropTypes.object
};

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = state => {
    return {
        statusValue: selector(state, 'status'),
        selectedEbonIdValue: selector(state, 'selectedEbonId'),
        i18nForm: state.i18n.feature.form,
        i18nPage: state.i18n.page.ACCOUNT.EBONS
    };
};

const ConnectedForm = connect(mapStateToProps)(EbonStatusUpdateForm);

export default reduxForm({
    form: FORM_NAME,
    destroyOnUnmount: false
})(ConnectedForm);



// WEBPACK FOOTER //
// ./src/components/pages/account/myebons/EbonStatusUpdateForm.js